import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    DynamoIconComponent,
    DynamoListMenuComponent,
    DynamoListMenuIconLibrary,
    DynamoListMenuItem,
    DynamoModule,
} from '@skillgmbh/dynamo';
import { CommonService } from '../../../../services/common/common.service';
import { ServiceService } from '../../../../services/service/service.service';
import { take } from 'rxjs';
import { ServiceType } from '../../../../lib/enums/ServiceType';
import { ServiceListFilter } from '../../../../lib/interfaces/ServiceListFilter';
import { DragScrollComponent, DragScrollItemDirective } from 'ngx-drag-scroll';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    standalone: true,
    selector: 'app-service-overview-filter',
    templateUrl: './service-overview-filter.component.html',
    styleUrl: './service-overview-filter.component.scss',
    imports: [DynamoListMenuComponent, DragScrollComponent, DragScrollItemDirective, DynamoModule, DynamoIconComponent],
})
export class ServiceOverviewFilterComponent implements AfterViewInit, OnChanges {
    @HostListener('window:scroll', ['$event'])
    onScroll(): void {
        if (this.commonService.isMobile(1000)) {
            this.isFilterFixed = false;
            return;
        }

        // This also references a value in the css file. It needs adjustment when this variable changes where comment is written.
        const headerHeight = 32;
        const scrollY: number = window.scrollY;
        const listHTMLElement = this.serviceOverviewFilterView?.nativeElement as HTMLElement;

        if (!this.isFilterFixed && listHTMLElement.offsetTop - scrollY < headerHeight) {
            this.listSnapPosition = listHTMLElement.offsetTop;
        }

        if (!this.isFilterFixed) {
            this.isFilterFixed = listHTMLElement.offsetTop - scrollY < headerHeight;
        } else {
            this.isFilterFixed = this.listSnapPosition - scrollY < headerHeight;
        }
    }

    @ViewChild('serviceOverviewFilterView', { read: ElementRef }) serviceOverviewFilterView?: ElementRef;
    @ViewChild('dragScrollComponent', { read: DragScrollComponent }) dragScrollComponent?: DragScrollComponent;

    @Input({ required: true }) addressSuggestion!: string;
    @Output() updateFilter: EventEmitter<ServiceListFilter> = new EventEmitter<ServiceListFilter>();

    protected citiesItems: DynamoListMenuItem[] = [];
    protected selectedCitiesItems: DynamoListMenuItem[] = [];
    protected serviceTypeItems: DynamoListMenuItem[] = [];
    protected selectedServiceTypeItems: DynamoListMenuItem[] = [];
    protected isFilterFixed: boolean = false;
    protected listSnapPosition: number = 0;
    protected index: number = 0;
    private serviceListFilter: ServiceListFilter = {
        serviceTypes: [],
        cities: [],
    };

    protected commonService: CommonService = inject(CommonService);
    protected serviceService: ServiceService = inject(ServiceService);
    private destroyRef: DestroyRef = inject(DestroyRef);

    ngAfterViewInit(): void {
        if (!this.commonService.IsPlatformBrowser()) return;

        this.fetchAvailableCities();
        this.fetchAvailableTypes();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['addressSuggestion']) {
            // TODO: Change the comparison criteria based on allowed delivery locations of each service in the future
            this.selectedCitiesItems = this.citiesItems.filter((cityListMenuItem: DynamoListMenuItem): boolean => {
                return (
                    this.addressSuggestion.toLowerCase().includes(cityListMenuItem.key.toLowerCase()) ||
                    cityListMenuItem.key.toLowerCase().includes(this.addressSuggestion.toLowerCase())
                );
            });

            this.serviceListFilter.cities = this.selectedCitiesItems.map(
                (cityListMenuItem: DynamoListMenuItem): string => cityListMenuItem.key,
            );

            this.selectedServiceTypeItems = [];

            this.updateFilter.emit(this.serviceListFilter);
        }
    }

    protected setSelectedCities(listMenuOptions: DynamoListMenuItem[]): void {
        this.serviceListFilter.cities =
            listMenuOptions?.map((listMenuOption: DynamoListMenuItem): string => listMenuOption.key) ?? [];

        this.selectedCitiesItems = listMenuOptions ?? [];

        this.updateFilter.emit(this.serviceListFilter);
    }

    protected setSelectedServiceTypes(listMenuOptions: DynamoListMenuItem[]): void {
        this.serviceListFilter.serviceTypes =
            listMenuOptions?.map((listMenuOption: DynamoListMenuItem): string => listMenuOption.key) ?? [];

        this.selectedServiceTypeItems = listMenuOptions ?? [];

        this.updateFilter.emit(this.serviceListFilter);
    }

    protected calculateScrollPercentage(scrollElement: HTMLElement): number {
        const maxScrollLeft: number = scrollElement.scrollWidth - scrollElement.clientWidth;
        const currentScroll: number = scrollElement.scrollLeft;

        // This calculates the maximum bar progress between current position of scrolling and 10%
        return Math.max((currentScroll / maxScrollLeft) * 100, 5);
    }

    protected getCitiesKeys(): string[] {
        if (!this.selectedCitiesItems.length) return [];

        return this.selectedCitiesItems.map((city: DynamoListMenuItem): string => city.key);
    }

    protected getServiceTypeKeys(): string[] {
        if (!this.selectedServiceTypeItems.length) return [];

        return this.selectedServiceTypeItems.map((serviceTypeItem: DynamoListMenuItem): string => serviceTypeItem.key);
    }

    protected hasFilterOptions(): boolean {
        return !!this.citiesItems.length || !!this.serviceTypeItems.length;
    }

    private fetchAvailableCities(): void {
        this.serviceService
            .fetchAvailableCities()
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe((cities: string[]): void => {
                cities.forEach((city: string): void => {
                    if (city == '') return;

                    this.citiesItems.push({
                        key: city,
                        name: city,
                        icon: {
                            type: 'location',
                            library: DynamoListMenuIconLibrary.DYNAMO,
                        },
                    });
                });
            });
    }

    private fetchAvailableTypes(): void {
        this.serviceService
            .fetchAvailableTypes()
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe((serviceTypes: ServiceType[]): void => {
                serviceTypes.forEach((type: ServiceType): void => {
                    this.serviceTypeItems.push({
                        key: type.toString(),
                        name: this.commonService.formatServiceType(type),
                        icon: {
                            type: type.toString().toLowerCase(),
                            library: DynamoListMenuIconLibrary.DYNAMO,
                        },
                    });
                });
            });
    }
}
